import parseMax from 'libphonenumber-js/mobile';
import { restCountries } from '../../../constants/restCountries';
import { ContactFormValues, FlattenedContactFormValues } from '../types';

export const getCountryFromPhoneNumber = (
  phone?: string,
): (typeof restCountries)[number] | null => {
  if (!phone || phone.includes('*')) return null;

  const parsed = parseMax(phone);

  if (!parsed?.country) return null;

  return (
    restCountries.find((c) => c.iso === parsed.country?.toUpperCase()) ?? null
  );
};

export const formatPhone = ({
  phone,
  countryPhoneCode,
}: {
  phone?: string;
  countryPhoneCode: string;
}) => {
  if (!phone) return;
  const updatePhone = phone.startsWith('+') ? phone : countryPhoneCode + phone;
  return parseMax(updatePhone)?.nationalNumber || phone;
};

export const FIELD_TYPES = { TEXT: 'text', DROPDOWN: 'dropdown' };

export const getAdditionalFields = (countryISO: string) => {
  const STATE = {
    name: 'state',
    field: 'state',
    type: FIELD_TYPES.DROPDOWN,
    isRequired: true,
    placeholder: 'ex. California',
  };
  const PROVINCE = {
    name: 'province',
    field: 'state',
    type: FIELD_TYPES.DROPDOWN,
    isRequired: true,
    placeholder: 'ex. Cairo',
  };
  const CITY = {
    name: 'city',
    field: 'city',
    type: FIELD_TYPES.DROPDOWN,
    isRequired: true,
    placeholder: 'ex. Abo Qir',
  };
  const EMIRATE = {
    name: 'emirate',
    field: 'state',
    type: FIELD_TYPES.DROPDOWN,
    isRequired: true,
    placeholder: 'ex. Dubai',
  };
  const PIN_CODE = {
    name: 'postal_code',
    field: 'postal_code',
    type: FIELD_TYPES.TEXT,
    isRequired: true,
    placeholder: 'ex. 12345',
  };

  switch (countryISO) {
    case 'AE':
      return [EMIRATE];
    case 'EG':
      return [PROVINCE, CITY, PIN_CODE];
    default:
      return [STATE, CITY, PIN_CODE];
  }
};

export const flattenContactFormValues = (
  defaultCountryISO: string,
  defaultValues?: Partial<ContactFormValues>
) => {
  if (!defaultValues) return;

  const result: FlattenedContactFormValues = {};

  if (defaultValues.name) {
    result.name = defaultValues.name;
  }
  if (defaultValues.email) {
    result.email = defaultValues.email;
  }
  if (defaultValues.use_same_as_billing) {
    result.use_same_as_billing = defaultValues.use_same_as_billing;
  }

  // a masked phone (e.g. a fast-checkout account number) cannot be parsed, so
  // prefilling it would show the mask under an unrelated country
  if (defaultValues.phone && !defaultValues.phone.includes('*')) {
    const phoneCountryISO =
      getCountryFromPhoneNumber(defaultValues.phone)?.iso || defaultCountryISO;
    result.phone =
      parseMax(defaultValues.phone, phoneCountryISO as any)?.nationalNumber ||
      defaultValues.phone;
  }

  if (defaultValues.shipping) {
    Object.entries(defaultValues.shipping).forEach(([key, value]) => {
      result[`shipping_${key}` as keyof FlattenedContactFormValues] =
        value as any;
    });

    if (result.shipping_phone_number) {
      // a number stored without a country code cannot be resolved on its own;
      // fall back to the address' own country so the value matches the country
      // the phone dropdown ends up showing
      const phoneCountryISO =
        getCountryFromPhoneNumber(result.shipping_phone_number)?.iso ||
        restCountries.find((c) => c.iso === defaultValues.shipping?.country)
          ?.iso ||
        defaultCountryISO;
      result.shipping_phone_number =
        parseMax(result.shipping_phone_number, phoneCountryISO as any)
          ?.nationalNumber || result.shipping_phone_number;
    }
  }

  if (defaultValues.billing) {
    Object.entries(defaultValues.billing).forEach(([key, value]) => {
      result[`billing_${key}` as keyof FlattenedContactFormValues] =
        value as any;
    });

    if (result.billing_phone_number) {
      const phoneCountryISO =
        getCountryFromPhoneNumber(result.billing_phone_number)?.iso ||
        restCountries.find((c) => c.iso === defaultValues.billing?.country)
          ?.iso ||
        defaultCountryISO;
      result.billing_phone_number =
        parseMax(result.billing_phone_number, phoneCountryISO as any)
          ?.nationalNumber || result.billing_phone_number;
    }
  }
  
  if(defaultValues.purpose) {
    result.purpose = defaultValues.purpose
  }

  return result;
};
